import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './RecruitmentDashboard.css';

import JDGenerator from './JDGenerator';
import GeneratedJDs from './GeneratedJDs';
import JobPosting from './JobPosting/JobPosting';
import PostedJobs from './PostedJobs';
import RecruiterApplicants from './JobPosting/RecruiterApplicants';
import MasterApplicants from './JobPosting/MasterApplicants';
import RecruiterProfile from './RecruiterProfile';

import {
  FileText,
  PlusSquare,
  Briefcase,
  UserCircle,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Building2,
  Users,
} from 'lucide-react';

const NAV_ITEMS = [
  { key: 'Generated JDs', label: "Generated JD's", icon: FileText },
  { key: 'Job Posting', label: 'Job Posting', icon: PlusSquare },
  { key: 'Posted Jobs', label: 'Posted Jobs', icon: Briefcase },
  { key: 'Master Inbox', label: 'Master Inbox', icon: Users },
  { key: 'Profile', label: 'Profile', icon: UserCircle },
];

const RecruitmentDashboard = () => {
  const [activeTab, setActiveTab] = useState('Posted Jobs');
  const [recruiterName, setRecruiterName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const rName = localStorage.getItem('recruiterName');
    const cName = localStorage.getItem('customerName');
    const cmpName = localStorage.getItem('companyName');

    if (rName) setRecruiterName(rName);
    else if (cName) setRecruiterName(cName);

    if (cmpName) setCompanyName(cmpName);

    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    const idParam = queryParams.get('id');

    if (tabParam === 'generator') {
      setActiveTab('JD Generator');
    } else if (idParam) {
      setActiveTab('Job Posting');
    }
  }, [location]);

  const handleLogout = () => {
    localStorage.clear();
    alert('Logged out successfully!');
    navigate('/');
  };

  // 🚨 FIX: Always reset viewingJob when clicking any sidebar tab
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setViewingJob(null); // CRITICAL: pulls you out of the applicants sub-view
    if (location.search) {
      navigate('/recruitment-dashboard');
    }
  };

  const renderContent = () => {
    if (viewingJob) {
      return (
        <RecruiterApplicants
          jobId={viewingJob.jobId}
          jobRole={viewingJob.jobRole}
          posterRole={viewingJob.posterRole}
          onBack={() => setViewingJob(null)}
        />
      );
    }

    switch (activeTab) {
      case 'JD Generator':
        return <JDGenerator />;
      case 'Generated JDs':
        return <GeneratedJDs />;
      case 'Job Posting':
        return <JobPosting onPostSuccess={setActiveTab} />;
      case 'Posted Jobs':
        return (
          <PostedJobs
            onEditClick={setActiveTab}
            onViewApplicants={setViewingJob}
            isRecruiter={true}
          />
        );
      case 'Master Inbox':
        return <MasterApplicants isRecruiter={true} />;
      case 'Profile':
        return <RecruiterProfile />;
      default:
        return <JDGenerator />;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'R';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  // Properly truncate company name
  const displayCompanyName = (name) => {
    if (!name) return 'Recruitment Portal';
    if (name.length > 22) return name.substring(0, 22) + '…';
    return name;
  };

  return (
    <div className="rd-layout">
      {/* ═══ Sidebar ═══ */}
      <aside className={`rd-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Top — Brand + Collapse */}
        <div className="rd-sidebar-top">
          <div
            className="rd-sidebar-brand"
            onClick={() => sidebarCollapsed && setSidebarCollapsed(false)}
          >
            <div className="rd-brand-icon">
              <Building2 size={22} />
            </div>
            {!sidebarCollapsed && (
              <div className="rd-brand-text">
                <span
                  className="rd-brand-name"
                  title={companyName || 'Recruitment Portal'}
                >
                  {displayCompanyName(companyName)}
                </span>
                <span className="rd-brand-sub">Recruiter Console</span>
              </div>
            )}
          </div>

          {!sidebarCollapsed && (
            <button
              className="rd-collapse-btn"
              onClick={() => setSidebarCollapsed(true)}
              title="Collapse sidebar"
            >
              <PanelLeftClose size={18} />
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <button
            className="rd-collapse-btn rd-expand-btn"
            onClick={() => setSidebarCollapsed(false)}
            title="Expand sidebar"
          >
            <PanelLeftOpen size={18} />
          </button>
        )}

        {/* Navigation */}
        <nav className="rd-sidebar-nav">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`rd-nav-item ${
                activeTab === key && !viewingJob ? 'active' : ''
              }`}
              onClick={() => handleTabClick(key)}
              title={sidebarCollapsed ? label : ''}
            >
              <Icon size={20} className="rd-nav-icon" />
              {!sidebarCollapsed && (
                <span className="rd-nav-label">{label}</span>
              )}
              {!sidebarCollapsed && activeTab === key && !viewingJob && (
                <span className="rd-nav-indicator" />
              )}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="rd-sidebar-footer">
          {!sidebarCollapsed && (
            <div
              className="rd-user-card"
              onClick={() => handleTabClick('Profile')}
            >
              <div className="rd-user-avatar">{getInitials(recruiterName)}</div>
              <div className="rd-user-info">
                <span className="rd-user-name" title={recruiterName}>
                  {recruiterName
                    ? recruiterName.length > 16
                      ? recruiterName.substring(0, 16) + '…'
                      : recruiterName
                    : 'Recruiter'}
                </span>
                <span className="rd-user-role">Recruiter</span>
              </div>
            </div>
          )}

          {sidebarCollapsed && (
            <div
              className="rd-user-avatar rd-user-avatar-collapsed"
              onClick={() => handleTabClick('Profile')}
              title={recruiterName || 'Profile'}
            >
              {getInitials(recruiterName)}
            </div>
          )}

          <button
            className="rd-logout-btn"
            onClick={handleLogout}
            title="Log out"
          >
            <LogOut size={18} />
            {!sidebarCollapsed && <span>Log Out</span>}
          </button>
        </div>
      </aside>

      {/* ═══ Main Content ═══ */}
      <main className="rd-main">
        <div className="rd-content">{renderContent()}</div>
      </main>
    </div>
  );
};

export default RecruitmentDashboard;